<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { stopPronunciation } from '../services/pronunciation'

const props = defineProps({ audio: { type: Object, required: true } })
const voices = ref([])
const supported = ref(false)
const status = ref('idle')
const rate = ref(0.85)
const error = ref('')
let utterance = null
const voice = computed(() => voices.value.find(item => item.lang === (props.audio.language || 'en-GB')) || voices.value.find(item => item.lang.toLowerCase().startsWith('en')))
const canPlay = computed(() => supported.value && voice.value)
function updateVoices() { voices.value = window.speechSynthesis.getVoices() }
function stop() {
  if (utterance && supported.value) window.speechSynthesis.cancel()
  utterance = null
  status.value = 'idle'
}
function play() {
  error.value = ''
  if (!canPlay.value) return
  stop()
  stopPronunciation()
  window.speechSynthesis.resume()
  const current = new window.SpeechSynthesisUtterance(props.audio.text)
  utterance = current
  current.lang = props.audio.language || 'en-GB'
  current.voice = voice.value
  current.rate = Number(rate.value)
  current.onend = () => { if (utterance === current) { status.value = 'idle'; utterance = null } }
  current.onerror = event => {
    if (utterance === current) {
      status.value = 'idle'
      utterance = null
      if (!['canceled', 'interrupted'].includes(event.error)) error.value = 'Chưa phát được giọng đọc. Em thử lại hoặc đối chiếu lời nghe cùng người lớn.'
    }
  }
  status.value = 'playing'
  window.speechSynthesis.speak(current)
}
function togglePause() {
  if (status.value === 'paused') { window.speechSynthesis.resume(); status.value = 'playing' }
  else { window.speechSynthesis.pause(); status.value = 'paused' }
}
watch(() => props.audio, stop)
onMounted(() => {
  supported.value = 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window
  if (supported.value) {
    updateVoices()
    window.speechSynthesis.addEventListener('voiceschanged', updateVoices)
  }
})
onUnmounted(() => {
  stop()
  if (supported.value) window.speechSynthesis.removeEventListener('voiceschanged', updateVoices)
})
</script>

<template>
  <div class="lesson-audio">
    <span class="audio-eyebrow">NGHE & ĐỐI CHIẾU</span><h3>{{ audio.title }}</h3>
    <p>Nghe giọng đọc của thiết bị. Thử ghi ý chính và chi tiết trước khi mở lời nghe.</p>
    <div class="audio-controls">
      <button type="button" class="btn btn-primary" :disabled="!canPlay" @click="play">{{ status === 'idle' ? '▶ Nghe' : '↻ Nghe lại' }}</button>
      <button v-if="status !== 'idle'" type="button" class="btn btn-light" @click="togglePause">{{ status === 'paused' ? 'Tiếp tục' : 'Tạm dừng' }}</button>
      <button v-if="status !== 'idle'" type="button" class="btn btn-light" @click="stop">Dừng</button>
      <label>Tốc độ <select v-model="rate" aria-label="Tốc độ giọng đọc"><option :value="0.7">Chậm</option><option :value="0.85">Vừa</option><option :value="1">Tự nhiên</option></select></label>
    </div>
    <p v-if="!canPlay" class="audio-notice" role="status">Thiết bị chưa có giọng đọc tiếng Anh phù hợp. Em có thể mở lời nghe và luyện đọc cùng người lớn.</p>
    <p v-if="error" class="audio-notice" role="alert">{{ error }}</p>
    <details :open="!audio.hide_transcript"><summary>Lời nghe tự biên soạn để đối chiếu</summary><p class="audio-transcript" lang="en">{{ audio.text }}</p></details>
  </div>
</template>

<style scoped>
.lesson-audio { margin: 22px 0; padding: 20px; border: 1px solid #cfcaed; border-radius: 16px; background: #f9f8ff; }
.audio-eyebrow { color: #6554a2; font-size: 10px; font-weight: 800; letter-spacing: 1px; }
h3 { margin: 7px 0 10px; font-size: 16px; }
p { font-size: 13px; line-height: 1.8; }
.audio-controls { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin: 14px 0; }
label { display: flex; gap: 7px; align-items: center; font-size: 12px; }
select { padding: 8px; border: 1px solid #d8d3eb; border-radius: 8px; background: white; }
details { margin-top: 14px; }
summary { cursor: pointer; font-size: 13px; font-weight: 700; }
.audio-transcript { white-space: pre-line; padding: 14px; border-radius: 10px; background: white; }
.audio-notice { color: #74613d; }
</style>
